import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { MedicalCondition } from "../types";
import { getApi } from "../api";
import { useAsyncResource } from "../hooks/useAsyncResource";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/Button";
import { ErrorState, Skeleton } from "../components/ScreenStates";
import { ConditionEditor } from "../components/actuarial/ConditionEditor";

/** `/uw/actuarial/conditions/edit?conditionId=…&ratingId=…`: one condition with all its ratings, focused on the rating asked for. */
export function ConditionEditorPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const conditionId = Number(params.get("conditionId"));
  const ratingParam = params.get("ratingId");
  const focusRatingId = ratingParam ? Number(ratingParam) : null;
  const condition = useAsyncResource(async () => (Number.isFinite(conditionId) && conditionId > 0 ? getApi().getCondition(conditionId) : Promise.reject(new Error("Choose a condition from the customizations list."))), String(conditionId), "ConditionEditorPage.condition");
  usePageSetup(condition.data?.name ?? "Edit condition", null);

  const backToList = useCallback(() => navigate("/uw/actuarial/conditions"), [navigate]);
  const shortcuts = useMemo(() => ({ cancel: backToList }), [backToList]);
  useShortcuts(shortcuts);

  const handleConditionChanged = (changed: MedicalCondition | null) => {
    if (changed) condition.reload();
    else backToList();
  };

  if (condition.data) return <ConditionEditor key={condition.data.id} condition={condition.data} focusRatingId={focusRatingId} onConditionChanged={handleConditionChanged} onBack={backToList} />;

  return (
    <>
      <PageHeader
        title="Edit condition"
        actions={
          <Button variant="secondary" onClick={backToList}>
            Back to customizations
          </Button>
        }
      />
      {condition.state.kind === "error" && <ErrorState message={condition.state.message} onRetry={condition.reload} />}
      {condition.state.kind === "loading" && (
        <div className="space-y-2" role="status" aria-label="Loading condition">
          <Skeleton className="h-10 w-1/2" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}
    </>
  );
}
